'use client'

import { useCallback, useMemo, useState } from 'react'
import { SearchInput, StatusPill, Tag, type PillTone } from '../ui'
import { baseDashboardClasses } from '../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { LAST_OPENED_ROW_CLASS } from './useListReturn'
import { ValueCell } from './ValueCell'
import { getEntryIsScam, getEntrySourceType } from './CasosView'
import type { Catalog, EntryRow } from './entryTypes'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'

// Extracted from UnifiedEntriesDashboardView (the search toolbar + table
// were previously inlined there) so a second caller — CampaignDetailPage's
// member-entries list — can show the exact same entry list UI/columns/
// drill-in behavior instead of recreating a lookalike. UnifiedEntriesDashboardView
// itself now renders this too; both callers own their own fetch, this just
// owns the shared search-filter + table rendering + row click contract.

const REVISION_LABEL: Record<string, string> = {
  unseen: 'Pendente',
  accept: 'Aceite',
  'auto-accept': 'Auto',
  'accept-hidden': 'Oculto',
  reject: 'Rejeitado',
  defer1: 'Adiado',
}

// unified_entries_flat.source_type — which of the three lifecycle stages
// (raw_entry → processed_entry → brand_campaign) this entry currently sits
// at, see the view's own migration comment. Cases has no equivalent column
// (raw_entries_flat is always just "raw_entry" from this table's point of
// view), so this stage chip is blank for Cases-sourced rows.
const STAGE_LABEL: Record<string, string> = {
  raw_entry: 'Raw',
  processed_entry: 'Processed',
  brand_campaign: 'Campaign',
}
const STAGE_TONE: Record<string, PillTone> = {
  raw_entry: 'neutral',
  processed_entry: 'signal',
  brand_campaign: 'success',
}

type PipelineStepTone = 'success' | 'error' | 'active' | 'neutral'

function pipelineStepTone(step: string): PipelineStepTone {
  if (step.endsWith('_failed')) return 'error'
  if (step.endsWith('_started')) return 'active'
  if (step.endsWith('_success')) return 'success'
  return 'neutral'
}

const PIPELINE_STEP_TONE: Record<PipelineStepTone, PillTone> = {
  success: 'success',
  error: 'danger',
  active: 'signal',
  neutral: 'neutral',
}
// The moderation status as a pill tone.
const REVISION_TONE: Record<string, PillTone> = {
  unseen: 'warning', accept: 'success', 'auto-accept': 'success', 'accept-hidden': 'neutral', reject: 'danger', defer1: 'neutral',
}

function humanizePipelineStep(step: string): string {
  return step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function fmtDate(iso?: string) {
  const d = parseDbTimestamp(iso)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

export function EntriesTable({
  entries,
  theme,
  catalog,
  total,
  onSelectEntry,
  onSelectCampaign,
  lastOpenedId = null,
  entryHref,
  searchPlaceholder = 'Filter by message, sender, domain, beneficiary…',
  query: controlledQuery,
  onQueryChange,
  extraToolbar,
  emptyMessage = 'No entries found',
  showIdColumns = false,
}: {
  entries: EntryRow[]
  theme: ProcessedTheme
  catalog: Catalog
  /** Denominator shown as "N / total" — omit to just show entries.length twice (no server-side total to report). */
  total?: number | null
  /** rowEl is the clicked <tr>, for useListReturn's scroll memory. */
  onSelectEntry: (entry: EntryRow, rowEl?: HTMLElement) => void
  /** The row to keep highlighted after coming back from its detail (see useListReturn). */
  lastOpenedId?: string | null
  /** The entry's own URL (useDetailPath's href). Given, the date cell is a
   * real link and Ctrl/Cmd-click or middle-click opens the entry in a new
   * tab; omitted (a campaign's member list), rows only open in place. */
  entryHref?: (entry: EntryRow) => string
  /** Supply to render a Campaign column that drills into the derived campaign
   * an entry belongs to. Omitted inside a campaign detail, where every row is
   * already the same campaign and the column would just repeat itself. */
  onSelectCampaign?: (campaignId: string) => void
  searchPlaceholder?: string
  /** Server-side search: when both are given the box is controlled by the
   * caller, which re-fetches on change (UnifiedEntriesDashboardView sends
   * it as /unified?q=), and the rows are shown as received — a client-side
   * filter over the loaded pages would hide matches on pages not loaded
   * yet. Omitted, the box filters the loaded rows locally (CampaignDetailPage,
   * whose members are all loaded). */
  query?: string
  onQueryChange?: (q: string) => void
  /** Extra control(s) rendered in the toolbar next to the search box — e.g. CampaignDetailPage's "filtered by <identifier> · clear" pill. */
  extraToolbar?: React.ReactNode
  emptyMessage?: string
  /** Domain / Beneficiary columns: the entry's latest domain and its entity
   * reference's beneficiary (last_domain_url, last_entity_reference_beneficiary),
   * "+N" when it has more. Entity and reference stay on the entry's detail.
   * On for Unified Entries. */
  showIdColumns?: boolean
}) {
  const { isDark, muted } = baseDashboardClasses(theme)
  const [localQuery, setLocalQuery] = useState('')
  const serverSearch = controlledQuery !== undefined && !!onQueryChange
  const query = serverSearch ? controlledQuery : localQuery
  const setQuery = serverSearch ? onQueryChange : setLocalQuery

  // Local filter over the same fields the server search covers (message,
  // sender, subject, domain, beneficiary, entity, reference), so both modes
  // answer the same question.
  const filtered = useMemo(() => {
    if (serverSearch || !query) return entries
    const q = query.toLowerCase()
    const has = (v: unknown) => String(v ?? '').toLowerCase().includes(q)
    return entries.filter((e) => {
      const raw = e.raw_entries ?? e
      const flat = (e.processed_entries_flat ?? {}) as Record<string, any>
      return (
        has(raw.message_content) || has(raw.sender) || has(raw.message_subject) ||
        has(flat.last_domain_url) || has(flat.last_entity_reference_beneficiary) ||
        has(flat.last_entity_reference_entity) || has(flat.last_entity_reference_reference)
      )
    })
  }, [entries, query, serverSearch])

  // The Campaign column shows the DERIVED campaign (campaign_type +
  // campaign_entry_count off the view), not the old curated campaign_name —
  // that column, and the rest of the curated metadata, was dropped when
  // campaigns stopped being stored. Without it a member of a multi-entry
  // campaign looks like an unrelated one-off in this list, with no route to
  // its co-members; clicking it opens the campaign, which lists them.
  //
  // Pipeline populates for Cases-sourced rows only: raw_entries_flat/
  // processed_entries_flat still carry last_pipeline_step, the rewritten
  // unified_entries_flat does not, so Unified/campaign-member rows show "—".
  // Three timestamps, all genuinely distinct on real data (458 rows where
  // created_at differs from the raw entry's, 2 where last_created_at differs
  // from created_at), so none of them is redundant:
  //   Created_At      — unified_entries_flat.created_at: the processed value
  //                     once an entry is processed, else the raw one.
  //   Last_Created_At — extends that chain to brand_campaign. This is what
  //                     the view sorts by, and what this table is ordered on,
  //                     so it leads alongside Created_At.
  //   Raw_Created_At  — the original raw_entries timestamp. Previously
  //                     mislabelled "Created_At": the row objects carry
  //                     raw_entry_created_at aliased onto `created_at` (see
  //                     mapUnifiedRowToEntryRow, which does that so the entry
  //                     detail page's raw.created_at keeps working), so the
  //                     old column was never showing the view's created_at.
  const columns: ColumnDef[] = useMemo(() => [
    { key: 'created_at', label: 'Created_At', initialWidth: 130 },
    { key: 'last_created_at', label: 'Last_Created_At', initialWidth: 130 },
    { key: 'stage', label: 'Stage', initialWidth: 90 },
    { key: 'last_pipeline_step', label: 'Pipeline', initialWidth: 150 },
    { key: 'source', label: 'Source', initialWidth: 120 },
    { key: 'platform', label: 'Platform', initialWidth: 110 },
    ...(showIdColumns ? [
      { key: 'domain', label: 'Domain', initialWidth: 180 },
      { key: 'beneficiary', label: 'Beneficiary', initialWidth: 180 },
    ] : []),
    ...(onSelectCampaign ? [{ key: 'campaign', label: 'Campaign', initialWidth: 170 }] : []),
    { key: 'raw_created_at', label: 'Raw_Created_At', initialWidth: 130 },
    { key: 'sender', label: 'Sender', initialWidth: 150 },
    { key: 'message_content', label: 'Message_Content', initialWidth: 300 },
    { key: 'revision_status', label: 'Status', initialWidth: 100 },
  ], [onSelectCampaign, showIdColumns])

  const platformName = useCallback(
    (platformId: number | null | undefined) => catalog.platforms.find((p) => p.id === platformId)?.name ?? null,
    [catalog.platforms],
  )

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background px-4 py-3.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <SearchInput value={query} onChange={setQuery} placeholder={searchPlaceholder} />
          {extraToolbar}
        </div>
        <span className={`font-mono text-label whitespace-nowrap ${muted}`}>
          {filtered.length} / {total ?? entries.length}
        </span>
      </div>
      <ResizableTable
        columns={columns}
        stickyHeader
        theme={theme}
        isEmpty={filtered.length === 0}
        emptyMessage={emptyMessage}
      >
        {filtered.map((e) => {
          const raw = e.raw_entries ?? e
          const source = getEntrySourceType(e)
          const isScam = getEntryIsScam(e)
          const status = e.revision_status ?? 'unseen'
          const platform = platformName(raw.platform_id)
          const stage = raw.source_type as string | undefined
          // The untouched view row — mapUnifiedRowToEntryRow aliases
          // raw_entry_created_at onto the top-level created_at, so the view's
          // own created_at/last_created_at have to be read from here rather
          // than off `raw`, or Created_At would silently show the raw
          // timestamp again.
          const flat = (e.processed_entries_flat ?? {}) as Record<string, any>
          const createdAt = flat.created_at ?? raw.created_at
          const lastCreatedAt = flat.last_created_at ?? raw.last_created_at
          // Compared as instants, not strings — the two columns come from
          // different expressions in the view and can serialize with
          // different precision even when they represent the same moment.
          const sameCreatedAt =
            createdAt != null && lastCreatedAt != null &&
            new Date(createdAt).getTime() === new Date(lastCreatedAt).getTime()
          return (
            <tr
              key={e.id}
              data-entry-id={e.id}
              data-last-opened={lastOpenedId === e.id || undefined}
              onClick={(ev) => {
                // Ctrl/Cmd-click anywhere on the row: the entry in a new tab,
                // the list stays (the date is a real link too, for the
                // right-click menu and middle-click)
                if (entryHref && (ev.ctrlKey || ev.metaKey)) { window.open(entryHref(e), '_blank', 'noopener'); return }
                onSelectEntry(e, ev.currentTarget)
              }}
              onAuxClick={(ev) => { if (entryHref && ev.button === 1) window.open(entryHref(e), '_blank', 'noopener') }}
              className={`cursor-pointer border-b border-border border-l-2 transition-colors ${
                isScam
                  ? 'border-l-destructive bg-danger-surface hover:bg-danger-surface/70'
                  : 'border-l-transparent hover:bg-secondary'
              } ${lastOpenedId === e.id ? LAST_OPENED_ROW_CLASS : ''}`}
            >
              <TableTd className={`font-mono whitespace-nowrap ${muted}`}>
                {entryHref ? (
                  // A real link to the entry's page (/{brand}/unified/{id}):
                  // right-click "open in new tab", middle-click and
                  // Ctrl/Cmd-click are the browser's own; a plain click stays
                  // in the app through the row.
                  <a href={entryHref(e)} data-action="entry-link" className="hover:underline"
                    onClick={(ev) => {
                      if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey || ev.button !== 0) { ev.stopPropagation(); return }
                      ev.preventDefault()   // the row's handler navigates in place and remembers the scroll
                    }}
                    onAuxClick={(ev) => ev.stopPropagation()}>{fmtDate(createdAt)}</a>
                ) : fmtDate(createdAt)}
              </TableTd>
              {/* last_created_at equals created_at on all but 2 of ~5200 rows
                  (it only diverges once an entry is promoted to a
                  brand_campaign whose timestamp differs), so repeating the
                  full value would just be a duplicate column of noise. Quiet
                  when it agrees, amber when it genuinely differs — which is
                  also the only case where it changes this table's ordering,
                  since the view sorts on it. */}
              <TableTd className="font-mono whitespace-nowrap">
                {lastCreatedAt == null ? (
                  <span className={muted}>—</span>
                ) : sameCreatedAt ? (
                  <span className="text-subtle" title={`Same as Created_At (${fmtDate(lastCreatedAt)})`}>=</span>
                ) : (
                  <span className="text-warning" title="Differs from Created_At — this entry was promoted to a brand_campaign with its own timestamp, and this is what the list is sorted by">
                    {fmtDate(lastCreatedAt)}
                  </span>
                )}
              </TableTd>
              <TableTd className="font-mono">
                {stage ? (
                  <StatusPill tone={STAGE_TONE[stage] ?? 'neutral'} dot={false}>{STAGE_LABEL[stage] ?? stage}</StatusPill>
                ) : (
                  <span className="text-subtle">—</span>
                )}
              </TableTd>
              <TableTd className="font-mono">
                {raw.last_pipeline_step ? (
                  <StatusPill tone={PIPELINE_STEP_TONE[pipelineStepTone(raw.last_pipeline_step)]}>{humanizePipelineStep(raw.last_pipeline_step)}</StatusPill>
                ) : (
                  <span className="text-subtle">—</span>
                )}
              </TableTd>
              <TableTd className={`font-mono truncate ${muted}`}>{source ?? '—'}</TableTd>
              <TableTd className={`font-mono truncate ${muted}`}>{platform ?? '—'}</TableTd>
              {showIdColumns && (
                <>
                  <TableTd><ValueCell value={flat.last_domain_url} extra={Math.max(0, (flat.entry_domain_count ?? 0) - 1)} mono isDark={isDark} /></TableTd>
                  <TableTd><ValueCell value={flat.last_entity_reference_beneficiary} extra={Math.max(0, (flat.entry_entity_reference_count ?? 0) - 1)} isDark={isDark} /></TableTd>
                </>
              )}
              {onSelectCampaign && (
                <TableTd className="font-mono">
                  {flat.campaign_id ? (
                    <button
                      type="button"
                      // The row itself opens the entry, so this has to claim
                      // the click rather than let it bubble into that.
                      onClick={(ev) => { ev.stopPropagation(); onSelectCampaign(flat.campaign_id) }}
                      title={`${flat.campaign_type ?? 'campaign'} — ${flat.campaign_entry_count ?? 1} ${(flat.campaign_entry_count ?? 1) === 1 ? 'entry' : 'entries'}. Open the campaign to see its other members.`}
                      className="inline-flex"
                    >
                      {(flat.campaign_entry_count ?? 1) > 1
                        ? <StatusPill tone="signal" dot={false} className="hover:bg-signal/20">{flat.campaign_type ?? 'campaign'} · {flat.campaign_entry_count ?? 1}</StatusPill>
                        : <Tag className="hover:text-signal">{flat.campaign_type ?? 'campaign'} · {flat.campaign_entry_count ?? 1}</Tag>}
                    </button>
                  ) : (
                    <span className="text-subtle">—</span>
                  )}
                </TableTd>
              )}
              <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(raw.raw_entry_created_at ?? raw.created_at)}</TableTd>
              <TableTd className={muted}>{raw.sender ?? '—'}</TableTd>
              <TableTd><span className={`line-clamp-1 ${muted}`}>{raw.message_content ?? '—'}</span></TableTd>
              <TableTd><StatusPill tone={REVISION_TONE[status] ?? 'neutral'}>{REVISION_LABEL[status] ?? status}</StatusPill></TableTd>
            </tr>
          )
        })}
      </ResizableTable>
    </>
  )
}
